/**
 * StatsPage — 成績・進捗・弱点分析 (FR-04 / FR-11 / FR-14 / FR-15 / EF-05)
 * カテゴリ別: 色+ラベル+数値 三重符号化 (#63)
 * 模試スコア推移: 折れ線（数値ラベル付き、SVG簡易実装）
 * 履歴削除 (EF-05): 確認ダイアログ → 再計算
 * 経験則 #68: 断定語なし
 */

import { useEffect, useState, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { getProgressOverview, getCategoryStats, getWeakAreas, listMockExamResults, listAnswerLogs, deleteAnswerLog } from '../api'
import { ApiClientError } from '../api/client'
import type { ProgressOverview, CategoryStat, WeakArea, MockExamResultSummary, CertificationType } from '../api/types'
import { AuthContext } from '../contexts/AuthContext'
import Icon from '../components/Icon'
import Spinner, { PageLoader } from '../components/Spinner'

function scoreBand(ratio: number): { label: string; text: string; bar: string } {
  if (ratio >= 0.8) return { label: '得意', text: 'text-band-high', bar: 'bg-band-high' }
  if (ratio >= 0.6) return { label: 'あと少し', text: 'text-band-mid', bar: 'bg-band-mid' }
  return { label: '要強化', text: 'text-band-low', bar: 'bg-band-low' }
}

/** 折れ線グラフ（SVG簡易実装）*/
function SparkLineChart({ results }: { results: MockExamResultSummary[] }) {
  if (results.length < 2) {
    return (
      <div className="py-2 text-[13px] text-text-muted">模試を2回以上受験すると推移が表示されます</div>
    )
  }

  const W = 280
  const H = 80
  const PAD = 20

  const ratios = results.map((r) => r.score_ratio ?? 0)
  const min = Math.min(...ratios)
  const max = Math.max(...ratios)
  const range = Math.max(max - min, 0.01)

  const pts = results.map((r, i) => {
    const x = PAD + (i / (results.length - 1)) * (W - PAD * 2)
    const y = H - PAD - (((r.score_ratio ?? 0) - min) / range) * (H - PAD * 2)
    return { x, y, ratio: r.score_ratio ?? 0 }
  })

  const d = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-20 w-full" aria-hidden="true">
      <path d={d} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="4" fill="var(--accent)" />
          <text x={p.x} y={p.y - 8} textAnchor="middle" fontSize="9" fill="var(--text-secondary)">
            {Math.round(p.ratio * 100)}%
          </text>
        </g>
      ))}
    </svg>
  )
}

export default function StatsPage() {
  const { certType } = useContext(AuthContext)
  const navigate = useNavigate()

  const [overview, setOverview] = useState<ProgressOverview | null>(null)
  const [categoryStats, setCategoryStats] = useState<CategoryStat[]>([])
  const [weakAreas, setWeakAreas] = useState<WeakArea[]>([])
  const [mockResults, setMockResults] = useState<MockExamResultSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadAll = (ct: CertificationType) => {
    setLoading(true)
    setError(null)
    Promise.all([
      getProgressOverview(ct),
      getCategoryStats(ct),
      getWeakAreas(ct),
      listMockExamResults(ct),
    ])
      .then(([ov, cs, wa, mr]) => {
        setOverview(ov)
        setCategoryStats(cs?.category_stats ?? [])
        setWeakAreas(wa?.weak_areas ?? [])
        const sorted = [...(mr?.mock_exam_results ?? [])].sort(
          (a, b) => new Date(a.taken_at ?? 0).getTime() - new Date(b.taken_at ?? 0).getTime(),
        )
        setMockResults(sorted)
      })
      .catch((err) => {
        if (err instanceof ApiClientError) setError(err.message)
        else setError('データの取得に失敗しました')
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (!certType) return
    loadAll(certType as CertificationType)
  }, [certType])

  const handleDeleteHistory = async () => {
    if (!certType) return
    if (!window.confirm('学習履歴をすべて削除しますか？\n削除した履歴は集計・弱点分析から除外され、元に戻せません。')) return

    setDeleting(true)
    setError(null)
    try {
      const logs = await listAnswerLogs({ certification_type: certType as CertificationType, limit: 500 })
      const items = logs?.answer_logs ?? []
      await Promise.all(items.map((l) => (l.id ? deleteAnswerLog(l.id) : Promise.resolve(null))))
      loadAll(certType as CertificationType)
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError('履歴の削除に失敗しました')
    } finally {
      setDeleting(false)
    }
  }

  if (!certType) return null

  const pct = overview?.overall_correct_ratio != null ? Math.round(overview.overall_correct_ratio * 100) : null
  const mockAvgPct = overview?.mock_avg_ratio != null ? Math.round(overview.mock_avg_ratio * 100) : null

  return (
    <div className="mx-auto max-w-content p-screen">
      <h1 className="mb-5 text-[22px] font-bold tracking-tight text-text-primary">成績</h1>

      {error && (
        <div role="alert" className="alert-error mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <PageLoader />
      ) : (
        <>
          {/* 総合概要 */}
          <section data-testid="stats-overview" className="mb-6">
            <div className="section-label">総合</div>
            <div className="card px-4 py-3.5">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-[11px] text-text-muted">総正答率</div>
                  <div className="mt-0.5 font-numeric text-[22px] font-bold">{pct != null ? `${pct}%` : '—'}</div>
                </div>
                <div>
                  <div className="text-[11px] text-text-muted">解答数</div>
                  <div className="mt-0.5 font-numeric text-[22px] font-bold">
                    {overview?.total_answered != null ? `${overview.total_answered}問` : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] text-text-muted">模試平均</div>
                  <div className="mt-0.5 font-numeric text-[22px] font-bold">{mockAvgPct != null ? `${mockAvgPct}%` : '—'}</div>
                </div>
              </div>
            </div>
          </section>

          {/* カテゴリ別 */}
          {categoryStats.length > 0 && (
            <section data-testid="category-stats" className="mb-6">
              <div className="section-label">カテゴリ別（正答率・解答数・閲覧済み）</div>
              <div className="overflow-hidden rounded-lg border border-border bg-surface">
                {categoryStats.map((cat, idx) => {
                  const ratio = cat.correct_ratio ?? 0
                  const band = scoreBand(ratio)
                  const viewedPct = cat.viewed_ratio != null ? Math.round(cat.viewed_ratio * 100) : null
                  return (
                    <div
                      key={cat.category_id}
                      className={idx < categoryStats.length - 1 ? 'border-b border-border px-4 py-3.5' : 'px-4 py-3.5'}
                    >
                      <div className="mb-1.5 flex items-baseline justify-between">
                        <span className="text-sm font-semibold">{cat.category_name_ja}</span>
                        <span className={`text-xs font-bold ${band.text}`}>
                          {Math.round(ratio * 100)}% {band.label}
                        </span>
                      </div>
                      {/* 正答率バー */}
                      <div className="mb-1.5 h-2 overflow-hidden rounded-pill bg-surface-alt">
                        <div
                          className={`h-full rounded-pill transition-[width] duration-300 ease-smooth ${band.bar}`}
                          style={{ width: `${Math.min(Math.round(ratio * 100), 100)}%` }}
                        />
                      </div>
                      <div className="flex gap-3 text-[11px] text-text-muted">
                        <span>解答 {cat.total_answered ?? 0}問</span>
                        {viewedPct != null && (
                          <span>
                            閲覧 {cat.viewed_texts ?? 0}/{cat.total_texts ?? 0}件 ({viewedPct}%)
                          </span>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {/* 苦手分野 */}
          {weakAreas.length > 0 && (
            <section data-testid="weak-areas" className="mb-6">
              <div className="section-label">苦手分野（正答率60%未満）</div>
              <div className="flex flex-col gap-2.5 rounded-lg border border-review bg-review-bg px-4 py-3.5">
                {weakAreas.map((wa) => (
                  <div key={wa.category_id} data-testid="weak-area-item" className="flex items-center justify-between gap-2">
                    <span className="text-sm text-text-primary">
                      {wa.category_name_ja}{' '}
                      <span className="font-bold text-review">{Math.round((wa.correct_ratio ?? 0) * 100)}%</span>
                    </span>
                    <div className="flex gap-1.5">
                      <button
                        onClick={() => navigate('/review')}
                        className="rounded-md bg-review px-2.5 py-1.5 text-xs font-semibold text-white transition-opacity hover:opacity-90"
                      >
                        復習する
                      </button>
                      <button
                        onClick={() => navigate(`/practice?category_id=${wa.category_id}`)}
                        className="rounded-md border border-border-strong bg-surface px-2.5 py-1.5 text-xs font-semibold text-text-primary transition-colors hover:bg-surface-alt"
                      >
                        演習する
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 模試スコア推移 */}
          <section data-testid="mock-history-chart" className="mb-6">
            <div className="section-label">模試スコア推移</div>
            <div className="card px-4 py-3.5">
              {mockResults.length === 0 ? (
                <div className="py-4 text-center text-[13px] text-text-muted">模擬試験を受験すると推移が表示されます</div>
              ) : (
                <SparkLineChart results={mockResults} />
              )}
              {mockResults.length > 0 && (
                <button
                  onClick={() => navigate('/mock-exam')}
                  className="mt-2.5 flex h-9 w-full items-center justify-center gap-1.5 rounded-md bg-surface-alt text-[13px] font-semibold text-accent transition-colors hover:bg-accent-subtle"
                >
                  <Icon name="clipboard" size={14} />
                  模擬試験を受験する
                </button>
              )}
            </div>
          </section>

          {/* 学習履歴の管理 (EF-05) */}
          <section className="mb-6">
            <div className="section-label">学習履歴の管理</div>
            <div className="card px-4 py-3.5">
              <p className="mb-3 text-[13px] leading-relaxed text-text-secondary">
                学習履歴を削除すると、集計・弱点分析から除外されます。 この操作は元に戻せません。
              </p>
              <button
                data-testid="history-delete"
                onClick={handleDeleteHistory}
                disabled={deleting}
                className="inline-flex items-center gap-2 rounded-md border border-incorrect bg-incorrect-bg px-4 py-2 text-[13px] font-semibold text-incorrect transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {deleting ? (
                  <Spinner size={14} label="削除中..." />
                ) : (
                  <>
                    <Icon name="trash" size={14} />
                    履歴を削除
                  </>
                )}
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  )
}
